import './signer-card-page.scss';
import PageHeader from 'modules/page-header';
import {
  ISignerInfo,
  PermissionToLabel,
  SignerPermissons,
} from 'constants/signers';
import { useState, useEffect, useCallback } from 'react';
import { Loader, IconBtn } from '@ru-digital-assets/ui-component';
import { useSelector, useDispatch } from 'hooks/redux';
import OperatorAPI from 'services/api/Operator';
import { setPush } from 'store/actions/app';
import PageCard from 'containers/PageCard';
import { useHistory } from 'react-router-dom';
import { getFormatedDate } from 'utils';
import { useParams } from 'react-router';
import { LightFooter, KeySelectionFooter, RadioButton } from 'components';
import { ArrowRight } from 'assets/icons';
import cn from 'classnames';
import { showLoader, hideLoader } from 'store/actions/loader';

enum ProcessStep {
  SELECT_PERMISSIONS = 'SELECT_PERMISSIONS',
  SIGN_PERMISSIONS = 'SIGN_PERMISSIONS',
}

export default function SignerCardPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const token = useSelector((s) => s.auth.account.token);
  const [signer, setSigner] = useState<ISignerInfo>();
  const [
    selectedPermission,
    setSelectedPermission,
  ] = useState<SignerPermissons>();
  const [processStep, setProcessStep] = useState(
    ProcessStep.SELECT_PERMISSIONS
  );
  const [isSignAvailable, setIsSignAvailable] = useState(false);

  const updateSigner = useCallback(
    (callback?: () => void) => {
      setSelectedPermission(undefined);
      setIsSignAvailable(false);
      OperatorAPI.getSigner(token, +id)
        .then((response) => {
          for (const permission of Object.values(response.data.permissions)) {
            if (permission.needSign) {
              setIsSignAvailable(true);
              break;
            }
          }
          setSigner(response.data);
        })
        .catch(() => {
          dispatch(
            setPush({
              type: 'error',
              message: 'Не удалось загрузить информацию об исполнителе',
            })
          );
        })
        .finally(callback);
    },
    [token, id]
  );

  useEffect(() => {
    updateSigner();
  }, [updateSigner]);

  const onSignPermissions = useCallback(
    (privateKey: string) => {
      dispatch(showLoader());
      OperatorAPI.signPermission(token, {
        userId: +id,
        permissionType: selectedPermission!,
        privateKey,
      })
        .then(() => {
          updateSigner(() => {
            dispatch(
              setPush({
                type: 'success',
                message: 'Полномочие успешно подписано',
              })
            );
            setProcessStep(ProcessStep.SELECT_PERMISSIONS);
            dispatch(hideLoader());
          });
        })
        .catch(() => {
          dispatch(
            setPush({
              type: 'error',
              message: 'Не удалось подписать полномочия',
            })
          );
        });
    },
    [token, id, selectedPermission, updateSigner]
  );

  const onClose = useCallback(() => {
    history.push('/profile/company', { tab: 'signers' });
  }, []);

  return (
    <div className="signer-card-page">
      <PageHeader
        items={[
          { link: '/profile/company', title: 'Профиль компании' },
          { link: '#', title: 'Карточка исполнителя' },
        ]}
        iconType="company"
        isHideBalanceInformer
      />
      {signer ? (
        <PageCard
          title="Добавление полномочия"
          onClose={onClose}
          minHeight="calc(100vh - 102px)"
          main={
            <div className="signer-card-page__main">
              <p className="p p_bold mb_16">Личные данные:</p>
              <p className="p p_font-size_18 mb_32">{signer.displayName}</p>
              <p className="p p_bold mb_16">
                {isSignAvailable && processStep !== ProcessStep.SIGN_PERMISSIONS
                  ? 'Выберите полномочия для подписания:'
                  : 'Полномочия:'}
              </p>
              {processStep === ProcessStep.SELECT_PERMISSIONS &&
                Object.values(signer.permissions).map((permission) => {
                  return (
                    <div
                      className={cn('signer-card-page__permission', {
                        'signer-card-page__permission_approved':
                          permission.isActive,
                      })}
                    >
                      {permission.needSign ? (
                        <RadioButton
                          label={PermissionToLabel[permission.type]}
                          checked={permission.type === selectedPermission}
                          disabled={permission.isActive}
                          onChange={() =>
                            setSelectedPermission(permission.type)
                          }
                        />
                      ) : (
                        <p
                          className={cn('p', {
                            'p_color_medium-grey': !permission.isActive,
                          })}
                        >
                          {PermissionToLabel[permission.type]}
                        </p>
                      )}
                      <div className="flex align-center">
                        <div className="mr_32">
                          <p className="p p_font-size_12 p_color_medium-grey">
                            Срок действия полномочий
                          </p>
                          <p className="p">
                            {getFormatedDate(
                              permission.expiryDate,
                              'dd MMMM yyyy'
                            )}
                          </p>
                        </div>
                        <p
                          className={cn('p p_color_medium-grey mr_24', {
                            'p_color_dark-green': permission.isActive,
                          })}
                          style={{ minWidth: '115px' }}
                        >
                          {permission.isActive ? 'Подписано' : 'На подписании'}
                        </p>
                        <p
                          className={cn(
                            'p p_color_medium-grey signer-card-page__permission-signs',
                            {
                              'p_color_dark-green': permission.isActive,
                            }
                          )}
                        >
                          {permission.countApprove}/{permission.totalThreshold}
                          <div className="signer-card-page__permission-tooltip">
                            <p className="p p_font-size_12 p_color_white mb_4">
                              Подпись согласанта -{' '}
                              {permission.signedBySigner ? '2' : '1'}/2
                            </p>
                            <p className="p p_font-size_12 p_color_white">
                              Подпись генерального директора -{' '}
                              {permission.signedByGeneralManager ? '1' : '0'}
                              /1
                            </p>
                          </div>
                        </p>
                      </div>
                    </div>
                  );
                })}
              {processStep === ProcessStep.SIGN_PERMISSIONS && (
                <div className={cn('signer-card-page__permission')}>
                  <p className={cn('p')}>
                    {PermissionToLabel[selectedPermission!]}
                  </p>
                  <div className="flex align-center">
                    <div className="mr_32">
                      <p className="p p_font-size_12 p_color_medium-grey">
                        Срок действия полномочий
                      </p>
                      <p className="p">
                        {getFormatedDate(
                          signer.permissions[selectedPermission!].expiryDate,
                          'dd MMMM yyyy'
                        )}
                      </p>
                    </div>
                    <p className={cn('p p_color_medium-grey mr_24')}>
                      На подписании
                    </p>
                    <p
                      className={cn(
                        'p p_color_medium-grey signer-card-page__permission-signs'
                      )}
                    >
                      {signer.permissions[selectedPermission!].countApprove}/
                      {signer.permissions[selectedPermission!].totalThreshold}
                      <div className="signer-card-page__permission-tooltip">
                        <p className="p p_font-size_12 p_color_white mb_4">
                          Подпись согласанта -{' '}
                          {signer.permissions[selectedPermission!]
                            .signedBySigner
                            ? '2'
                            : '1'}
                          /2
                        </p>
                        <p className="p p_font-size_12 p_color_white">
                          Подпись генерального директора -{' '}
                          {signer.permissions[selectedPermission!]
                            .signedByGeneralManager
                            ? '1'
                            : '0'}
                          /1
                        </p>
                      </div>
                    </p>
                  </div>
                </div>
              )}
              {isSignAvailable && (
                <p className="p">
                  Полномочия необходимо подписать Ключом, иначе данные не
                  сохранятся.
                </p>
              )}
            </div>
          }
          footer={
            isSignAvailable ? (
              processStep === ProcessStep.SELECT_PERMISSIONS ? (
                <LightFooter>
                  <div className="flex justify-between align-center">
                    <button className="button button_delete" onClick={onClose}>
                      Отмена
                    </button>
                    <IconBtn
                      title="Перейти к подписанию"
                      disabled={!selectedPermission}
                      icon={<ArrowRight color="#fff" />}
                      onClick={() =>
                        setProcessStep(ProcessStep.SIGN_PERMISSIONS)
                      }
                    />
                  </div>
                </LightFooter>
              ) : (
                <KeySelectionFooter
                  onBackButtonClick={() =>
                    setProcessStep(ProcessStep.SELECT_PERMISSIONS)
                  }
                  backButtonText="Вернуться"
                  submitButtonTitle={
                    'Подписать транзакцию\nи добавить исполнителя'
                  }
                  onSubmit={onSignPermissions}
                />
              )
            ) : null
          }
        />
      ) : (
        <div className="flex justify-center mt_32">
          <Loader />
        </div>
      )}
    </div>
  );
}
